import { Lightning } from '@lightningjs/sdk';
import { ButtonTemplateSpec } from 'components/common/buttons/Button';
import { getFontFaceFromStyle } from 'support/textUtils';
import ActionsList from 'components/common/ActionsList';
import { constants } from 'aliases';
import { HoverableComponent } from 'components/common/HoverableComponent';
import { getImageTextureObj } from 'support/generalUtils';
import TextLabel from 'components/common/TextLabel';

export interface NotificationTemplateSpec
  extends Lightning.Component.TemplateSpec {
  imageUrl: string;
  titleLabel: string;
  descLabel: string;
  backgroundColor: number;
  textColor: number;

  Content: {
    Image: Lightning.textures.ImageTexture;
    HeadingContainer: {
      Heading: typeof TextLabel;
      Description: typeof TextLabel;
    };
    Actions: typeof ActionsList;
  };
}

export const FULLSCREEN_BUTTON_WIDTH = 800;

const FULLSCREEN_WIDTH = 1920;
const FULLSCREEN_HEIGHT = 1080;
const HEADING_WIDTH = 1000;
const HEADING_Y = 100;
const ACTIONS_Y = 846;
const HEADING_FONT_SIZE = 32;
const DESCRIPTION_FONT_SIZE = 20;
const HEADING_MARGIN = 20;
const BUTTONS_SPACE = 10;

export default class FullscreenNotification
  extends HoverableComponent<NotificationTemplateSpec>
  implements
    Lightning.Component.ImplementTemplateSpec<NotificationTemplateSpec>
{
  private _Content = this.getByRef('Content')!;
  private _HeadingContainer = this._Content.getByRef('HeadingContainer')!;
  private _Heading = this._HeadingContainer.getByRef('Heading')!;
  private _Description = this._HeadingContainer.getByRef('Description')!;
  private _Image = this._Content.getByRef('Image')!;
  private _Actions = this._Content.getByRef('Actions')!;

  static override _template(): Lightning.Component.Template<NotificationTemplateSpec> {
    return {
      w: FULLSCREEN_WIDTH,
      h: FULLSCREEN_HEIGHT,
      rect: true,
      visible: false,
      zIndex: constants.ui.zIndex.notificationLayer,
      Content: {
        w: FULLSCREEN_WIDTH,
        h: FULLSCREEN_HEIGHT,
        Image: {
          w: FULLSCREEN_WIDTH,
          h: FULLSCREEN_HEIGHT,
        },
        HeadingContainer: {
          zIndex: constants.ui.zIndex.notificationLayer + 1,
          mountX: 0.5,
          x: FULLSCREEN_WIDTH / 2,
          y: HEADING_Y,
          w: HEADING_WIDTH,
          flex: {
            direction: 'column',
            alignItems: 'center',
          },
          Heading: {
            type: TextLabel,
            flexItem: {
              marginBottom: HEADING_MARGIN,
            },
            text: {
              fontSize: HEADING_FONT_SIZE,
              fontFace: getFontFaceFromStyle('bold'),
              textAlign: 'center',
            },
          },
          Description: {
            type: TextLabel,
            text: {
              wordWrap: true,
              wordWrapWidth: HEADING_WIDTH,
              fontSize: DESCRIPTION_FONT_SIZE,
              fontFace: getFontFaceFromStyle('regular'),
              maxLines: 2,
              lineHeight: 24,
              textAlign: 'center',
            },
          },
        },
        Actions: {
          type: ActionsList,
          mountX: 0.5,
          x: FULLSCREEN_WIDTH / 2,
          y: ACTIONS_Y,
          zIndex: constants.ui.zIndex.notificationLayer + 1,
          flex: {
            direction: 'column',
            alignItems: 'center',
          },
          spacing: BUTTONS_SPACE,
          signals: {},
          items: [],
        },
      },
    };
  }

  set imageUrl(url: string) {
    this._Image.patch(
      getImageTextureObj(url, FULLSCREEN_WIDTH, FULLSCREEN_HEIGHT),
    );
  }

  set titleLabel(header: string) {
    this._Heading.patch({ text: header, visible: !!header });
  }

  set descLabel(description: string) {
    this._Description.patch({ text: description, visible: !!description });
  }

  set backgroundColor(color: number) {
    this.patch({ color });
  }

  set textColor(color: number) {
    this._Heading.patch({ text: { textColor: color } });
    this._Description.patch({ text: { textColor: color } });
  }

  get announce() {
    const heading = this._Heading.text?.text;
    const desc = this._Description.text?.text;

    return [heading, desc];
  }

  override _getFocused() {
    return this._Actions;
  }

  override _handleClick(target: Lightning.Component) {
    if (target !== this._Actions) {
      return true;
    }
  }

  updateButtons(
    buttons: Lightning.Element.PatchTemplate<ButtonTemplateSpec>[],
  ) {
    if (!this._Actions || !buttons) return;

    const signals: Record<string, true> = {};
    buttons.forEach(({ action }) => {
      if (action) signals[action] = true;
    });

    this._Actions.patch({
      items: buttons,
      passSignals: signals,
    });
    this.signals = signals;
  }
}
